import { clsx } from 'clsx';
import { useEffect, useMemo, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';

import type { LcpnTheme } from '../../styles/theme';
import { useMapData } from '../../map-data/use-map-data';
import { usePoiCountryHierarchy } from '../../map-data/use-poi-hierarchy';
import { usePoiSelectedLocations } from '../PointOfInterestExperience/PoiSelectedLocationsProvider';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import { Scrollbar } from '../Scrollbar';
import { ListHeading } from '../ListHeading';
import { useUpdateNavigationBar } from '../NavigationBar/NavigationBarProvider';
import { usePoiFilterOptions } from '../../map-data/use-filter-options';

import { SubDivisionList } from './SubDivisionList';
import {
  POI_COUNTRY_LIST_ID,
  POI_COUNTRY_LIST_ITEM_ID_PREFIX,
  SubDivision,
} from './constants';
import { getCountryListScrollContainer } from './get-list-scroll-container';

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
    userSelect: 'none',
    overflow: 'hidden',
    height: '100%',
  },

  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0, 3),
    height: '3.0625rem',
    cursor: 'pointer',
    '& > div': {
      borderBottom: `${theme.colors['white']} solid 1px`,
    },
    '& > div:last-child': {
      borderBottom: `none`,
    },
    '&:hover': {
      backgroundColor: theme.colors['green-100'],
      color: theme.colors.white,
    },
    '&:focus-visible': {
      outline: 'auto',
    },
    borderBottom: `${theme.colors['white']} solid 2px`,
  },

  group: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },

  listCollapse: {
    backgroundColor: theme.colors['green-10'],
  },

  listExpand: {
    backgroundColor: theme.colors['green-100'],
    color: theme.colors.white,
    borderBottom: 'none',
    '& > div': {
      borderBottom: 'none',
    },
  },

  icon: {
    marginRight: theme.spacing(8),
  },

  row: {
    display: 'flex',
    flexDirection: 'column',

    marginBottom: 0,
  },
}));

export interface CountryCount {
  country: number;
  [SubDivision.region]: Record<string, number>;
  [SubDivision.city]: Record<string, number>;
}

export function PointOfInterestCountryList() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { loading, poiCountryData, poiCityData, studiesById } = useMapData();
  const { resetFilterOptions: resetPoiMapFilters, filterOptions } =
    usePoiFilterOptions();
  const {
    selectedCountryName,
    setSelectedCountryName,
    setShowCountryCard,
    setShowProgramCard,
    setShowCityCard,
    setSelectedProgramId,
    setSelectedCountrySubDivisionName,
    selectedCountrySubDivisionName,
    selectedProgramId,
    resetExperience,
  } = usePoiSelectedLocations();
  const { resetNavigationBar } = useUpdateNavigationBar();

  const hasFiltersApplied = Object.keys(filterOptions).length > 0;

  // handle navigation via address bar query strings
  const hasRunQsRef = useRef(false); // pointer to keep track of whether the query string has been run
  useEffect(() => {
    if (hasRunQsRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const qsId = params.get('poiId');
    if (!!qsId && !!studiesById) {
      setSelectedProgramId(qsId);
      setShowProgramCard(true);

      const selectedStudy = studiesById[qsId];
      setSelectedCountryName(selectedStudy?.country);
      setSelectedCountrySubDivisionName({
        city: selectedStudy?.city,
        region: selectedStudy?.region,
      });
      hasRunQsRef.current = true;
    }
  }, [
    setSelectedProgramId,
    setShowProgramCard,
    setSelectedCountryName,
    setSelectedCountrySubDivisionName,
    studiesById,
  ]);

  // Use shared hierarchy hook for DRY code
  const { countries: hierarchyCountries } = usePoiCountryHierarchy();
  const uniqueStudyCountries = hierarchyCountries.map(country => country.name);

  // Use hierarchy data for subdivisions
  const { filteredSubDivisions, subDivisionType } = useMemo<{
    filteredSubDivisions?: string[];
    subDivisionType?: SubDivision;
  }>(() => {
    if (!selectedCountryName) {
      return {
        filteredSubDivisions: undefined,
      };
    }

    const selectedHierarchyCountry = hierarchyCountries.find(
      country => country.name === selectedCountryName,
    );

    if (!selectedHierarchyCountry) {
      return {
        filteredSubDivisions: undefined,
      };
    }

    return {
      filteredSubDivisions: selectedHierarchyCountry.subdivisions.map(
        sub => sub.name,
      ),
      subDivisionType: selectedHierarchyCountry.hasLargeStudySet
        ? SubDivision.region
        : SubDivision.city,
    };
  }, [hierarchyCountries, selectedCountryName]);

  // Convert hierarchy data to the format expected by the existing UI components
  const studyCount = useMemo(() => {
    const counts: Record<string, CountryCount> = {};

    for (const country of hierarchyCountries) {
      counts[country.name] = {
        country: country.totalStudyCount,
        [SubDivision.region]: {},
        [SubDivision.city]: {},
      };

      for (const subdivision of country.subdivisions) {
        if (subdivision.isRegion) {
          counts[country.name][SubDivision.region][subdivision.name] =
            subdivision.studyCount;
        } else {
          counts[country.name][SubDivision.city][subdivision.name] =
            subdivision.studyCount;
        }
      }
    }

    return counts;
  }, [hierarchyCountries]);

  // Scroll to selected country when it changes. Only scroll at country level when no
  // study or subdivision is selected (more specific scrolls take precedence)
  useEffect(() => {
    if (!selectedCountryName) {
      return;
    }

    // Don't scroll at country level if a subdivision is selected (let SubDivisionList handle it)
    // But DO scroll if only a program is selected, to ensure country is visible
    if (
      selectedCountrySubDivisionName?.city ||
      selectedCountrySubDivisionName?.region
    ) {
      return;
    }

    const scrollContainer = getCountryListScrollContainer();
    if (!scrollContainer) {
      return;
    }

    // Delay to ensure DOM is fully rendered with expanded items
    // Increased to 500ms to allow for collapse/expand animations to complete
    const scrollTimeout = setTimeout(() => {
      const countryIndex = uniqueStudyCountries.indexOf(selectedCountryName);
      if (countryIndex === -1) {
        return;
      }

      const listItem = document.querySelector<HTMLLIElement>(
        `#${POI_COUNTRY_LIST_ITEM_ID_PREFIX}-${countryIndex}`,
      );

      if (!listItem) {
        return;
      }

      // Verify element has dimensions before scrolling
      const listItemRect = listItem.getBoundingClientRect();
      if (listItemRect.height === 0) {
        return;
      }

      const containerRect = scrollContainer.getBoundingClientRect();

      // Calculate the target scroll position to show item at top
      const targetScrollTop =
        scrollContainer.scrollTop + (listItemRect.top - containerRect.top);

      scrollContainer.scrollTo({
        behavior: 'smooth',
        top: targetScrollTop,
      });
    }, 500);

    return () => clearTimeout(scrollTimeout);
  }, [
    selectedCountryName,
    uniqueStudyCountries,
    selectedProgramId,
    selectedCountrySubDivisionName,
  ]);

  if (loading || !poiCountryData || !poiCityData) {
    // handle loading here
    return null;
  }

  const handleToggle = (name: string) => {
    setSelectedCountryName(name === selectedCountryName ? '' : name);
    setShowCountryCard(true);
    setShowProgramCard(false);
    setShowCityCard(false);
    setSelectedProgramId(undefined);
    setSelectedCountrySubDivisionName(undefined);
    resetNavigationBar();
  };

  const handleReset = () => {
    resetExperience();
    resetPoiMapFilters();
    getCountryListScrollContainer()?.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
  };

  return (
    <div className={classes.list} data-tour="country-list">
      <ListHeading
        label={t('countryListView.counter', {
          defaultValue: 'Showing {{ count }} countries with entries',
          replace: {
            count: uniqueStudyCountries?.length,
          },
        })}
        navigationVisible={!!selectedCountryName || hasFiltersApplied}
        navigationAction={() => handleReset()}
        navigationLabel={t('countryListView.reset')}
      />
      <Scrollbar>
        <ul id={POI_COUNTRY_LIST_ID}>
          {uniqueStudyCountries?.map((studyCountry, index) => (
            <li className={classes.row} key={studyCountry}>
              <button
                className={clsx(
                  classes.listItem,
                  {
                    [classes.listCollapse]:
                      studyCountry !== selectedCountryName,
                  },
                  {
                    [classes.listExpand]: studyCountry === selectedCountryName,
                  },
                )}
                id={`${POI_COUNTRY_LIST_ITEM_ID_PREFIX}-${index}`}
                onClick={() => {
                  handleToggle(studyCountry);
                }}
                key={studyCountry}
              >
                <div className={classes.group}>
                  <Icon
                    variant={
                      studyCountry === selectedCountryName
                        ? 'CollapseList'
                        : 'ExpandList'
                    }
                    fill="#fff"
                    stroke="#6a6a6a"
                    width={20}
                    height={20}
                  />
                  <>
                    <Typography variant="h3">{studyCountry}</Typography>
                    <Typography variant="p1">
                      ({studyCount[studyCountry].country ?? ''})
                    </Typography>
                  </>
                </div>
              </button>
              {studyCountry === selectedCountryName &&
                filteredSubDivisions &&
                subDivisionType && (
                  <SubDivisionList
                    subDivisions={filteredSubDivisions}
                    subDivisionType={subDivisionType}
                    studyCountry={studyCountry}
                    studyCountByCountry={studyCount}
                    countryIndex={index}
                  />
                )}
            </li>
          ))}
        </ul>
      </Scrollbar>
    </div>
  );
}
